import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Box, Rating, Tabs, Tab, Grid, TextField, MenuItem, FormControl, InputLabel, Select, Typography, Chip, Stack } from '@mui/material'
import { Add, Edit, Delete, Star, Assessment, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal, ExportButtons } from '@components/common'
import { VendorForm } from '@components/vendors'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

const vendorColumns = [
  { field: 'code', headerName: 'Code', sortable: true, width: 100 },
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'contact_person', headerName: 'Contact Person', sortable: true },
  { field: 'email', headerName: 'Email', sortable: true },
  { field: 'phone', headerName: 'Phone', sortable: true },
  {
    field: 'vendor_type',
    headerName: 'Type',
    sortable: true,
    renderCell: (value) => value?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-',
  },
  {
    field: 'rating',
    headerName: 'Rating',
    renderCell: (value) => <Rating value={value} size="small" readOnly />,
  },
  {
    field: 'is_active',
    headerName: 'Status',
    renderCell: (value) => (
      <StatusChip status={value ? 'active' : 'inactive'} />
    ),
  },
]

const performanceColumns = [
  {
    field: 'evaluation_date',
    headerName: 'Date',
    sortable: true,
    renderCell: (value) => value ? formatDate(value) : '-',
  },
  { field: 'vendor_name', headerName: 'Vendor', sortable: true },
  {
    field: 'rating',
    headerName: 'Overall Rating',
    renderCell: (value) => (
      <Box display="flex" alignItems="center" gap={1}>
        <Rating value={value} size="small" readOnly />
        <Typography variant="body2">({value}/5)</Typography>
      </Box>
    ),
  },
  {
    field: 'quality_score',
    headerName: 'Quality',
    renderCell: (value) => value ? (
      <Chip
        label={`${value}/5`}
        size="small"
        color={value >= 4 ? 'success' : value >= 3 ? 'warning' : 'error'}
      />
    ) : '-',
  },
  {
    field: 'cost_accuracy',
    headerName: 'Cost Accuracy',
    renderCell: (value) => value ? (
      <Chip
        label={`${value}/5`}
        size="small"
        color={value >= 4 ? 'success' : value >= 3 ? 'warning' : 'error'}
      />
    ) : '-',
  },
  {
    field: 'response_time_hours',
    headerName: 'Response Time',
    renderCell: (value) => value ? `${value} hrs` : '-',
  },
  { field: 'evaluated_by_name', headerName: 'Evaluated By' },
]

const VendorsList = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [vendors, setVendors] = useState([])
  const [performances, setPerformances] = useState([])
  const [workOrders, setWorkOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [performanceFormOpen, setPerformanceFormOpen] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [selectedPerformance, setSelectedPerformance] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteType, setDeleteType] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [performanceFormData, setPerformanceFormData] = useState({
    vendor: '',
    work_order: '',
    rating: 3,
    quality_score: 3,
    cost_accuracy: 3,
    response_time_hours: '',
    comments: '',
  })

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  // Filter states
  const [filters, setFilters] = useState({
    vendor_type: '',
    is_active: '',
  })

  // Refs for stable callbacks
  const filtersRef = useRef(filters)
  const searchTermRef = useRef(searchTerm)
  const pageSizeRef = useRef(pageSize)

  // Export columns for vendors
  const vendorExportColumns = useMemo(() => [
    { field: 'code', headerName: 'Code' },
    { field: 'name', headerName: 'Name' },
    { field: 'contact_person', headerName: 'Contact Person' },
    { field: 'email', headerName: 'Email' },
    { field: 'phone', headerName: 'Phone' },
    { field: 'vendor_type', headerName: 'Type', renderValue: (v) => v?.replace('_', ' ') || '' },
    { field: 'rating', headerName: 'Rating', renderValue: (v) => v ? `${v}/5` : '' },
    { field: 'is_active', headerName: 'Status', renderValue: (v) => v ? 'Active' : 'Inactive' },
    { field: 'address', headerName: 'Address' },
  ], [])

  // Export columns for performances
  const performanceExportColumns = useMemo(() => [
    { field: 'evaluation_date', headerName: 'Date', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'vendor_name', headerName: 'Vendor' },
    { field: 'rating', headerName: 'Overall Rating', renderValue: (v) => v ? `${v}/5` : '' },
    { field: 'quality_score', headerName: 'Quality Score', renderValue: (v) => v ? `${v}/5` : '' },
    { field: 'cost_accuracy', headerName: 'Cost Accuracy', renderValue: (v) => v ? `${v}/5` : '' },
    { field: 'response_time_hours', headerName: 'Response Time', renderValue: (v) => v ? `${v} hrs` : '' },
    { field: 'evaluated_by_name', headerName: 'Evaluated By' },
    { field: 'comments', headerName: 'Comments' },
  ], [])

  // Keep refs in sync
  useEffect(() => { filtersRef.current = filters }, [filters])
  useEffect(() => { searchTermRef.current = searchTerm }, [searchTerm])
  useEffect(() => { pageSizeRef.current = pageSize }, [pageSize])

  const fetchData = useCallback(async (currentPage, currentPageSize, search) => {
    try {
      setLoading(true)
      const currentFilters = filtersRef.current
      const vendorParams = {
        page: currentPage + 1,
        page_size: currentPageSize,
      }
      if (search) vendorParams.search = search
      if (currentFilters.vendor_type) vendorParams.vendor_type = currentFilters.vendor_type
      if (currentFilters.is_active !== '') vendorParams.is_active = currentFilters.is_active

      const [vendorsRes, performancesRes, workOrdersRes] = await Promise.all([
        api.get('/vendors/', { params: vendorParams }),
        api.get('/vendors/performance/', { params: { page_size: 100 } }),
        api.get('/maintenance/work-orders/', { params: { status: 'completed', page_size: 100 } }),
      ])
      setVendors(vendorsRes.data.results || vendorsRes.data)
      setTotalCount(vendorsRes.data.count || 0)
      setPerformances(performancesRes.data.results || performancesRes.data)
      setWorkOrders(workOrdersRes.data.results || workOrdersRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const handlePaginationChange = useCallback(({ page: newPage, pageSize: newPageSize }) => {
    setPage(newPage)
    setPageSize(newPageSize)
    fetchData(newPage, newPageSize, searchTermRef.current)
  }, [fetchData])

  const handleSearch = useCallback((search) => {
    setSearchTerm(search)
    setPage(0)
    fetchData(0, pageSizeRef.current, search)
  }, [fetchData])

  // Initial load and when filters change
  useEffect(() => {
    setPage(0)
    setSearchTerm('')
    fetchData(0, pageSizeRef.current, '')
  }, [filters, fetchData])

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ vendor_type: '', is_active: '' })
  }

  const hasActiveFilters = filters.vendor_type || filters.is_active !== ''

  const getActiveFilterLabels = () => {
    const labels = {}
    if (filters.vendor_type) labels.Type = filters.vendor_type.replace('_', ' ')
    if (filters.is_active !== '') labels.Status = filters.is_active === 'true' ? 'Active' : 'Inactive'
    return labels
  }

  const handleAddClick = () => {
    if (activeTab === 0) {
      setSelectedVendor(null)
      setFormOpen(true)
    } else {
      setSelectedPerformance(null)
      setPerformanceFormData({
        vendor: '',
        work_order: '',
        rating: 3,
        quality_score: 3,
        cost_accuracy: 3,
        response_time_hours: '',
        comments: '',
      })
      setPerformanceFormOpen(true)
    }
  }

  const handleFormSuccess = () => {
    fetchData(page, pageSize, searchTerm)
    setSelectedVendor(null)
  }

  const handleEditVendor = (vendor) => {
    setSelectedVendor(vendor)
    setFormOpen(true)
  }

  const handleEditPerformance = (performance) => {
    setSelectedPerformance(performance)
    setPerformanceFormData({
      vendor: performance.vendor || '',
      work_order: performance.work_order || '',
      rating: performance.rating || 3,
      quality_score: performance.quality_score || 3,
      cost_accuracy: performance.cost_accuracy || 3,
      response_time_hours: performance.response_time_hours || '',
      comments: performance.comments || '',
    })
    setPerformanceFormOpen(true)
  }

  const handleSavePerformance = async () => {
    if (!performanceFormData.vendor || !performanceFormData.rating) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (selectedPerformance) {
        await api.patch(`/vendors/performance/${selectedPerformance.id}/`, performanceFormData)
        toast.success('Performance review updated')
      } else {
        await api.post('/vendors/performance/', performanceFormData)
        toast.success('Performance review added')
      }
      setPerformanceFormOpen(false)
      fetchData(page, pageSize, searchTerm)
    } catch (error) {
      console.error('Error saving performance:', error)
      toast.error('Failed to save performance review')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteClick = (item, type) => {
    if (type === 'vendor') {
      setSelectedVendor(item)
      setDeleteType('vendor')
    } else {
      setSelectedPerformance(item)
      setDeleteType('performance')
    }
    setDeleteModalOpen(true)
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      if (deleteType === 'vendor') {
        await api.delete(`/vendors/${selectedVendor.id}/`)
        toast.success('Vendor deleted')
      } else {
        await api.delete(`/vendors/performance/${selectedPerformance.id}/`)
        toast.success('Performance review deleted')
      }
      fetchData(page, pageSize, searchTerm)
      setDeleteModalOpen(false)
      setSelectedVendor(null)
      setSelectedPerformance(null)
    } catch (error) {
      console.error('Error deleting:', error)
      toast.error('Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  const vendorActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditVendor(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'vendor')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const performanceActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditPerformance(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'performance')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const getAddButtonText = () => {
    return activeTab === 0 ? 'Add Vendor' : 'Add Review'
  }

  const getDeleteItemName = () => {
    if (deleteType === 'vendor') return selectedVendor?.name
    return `review for ${selectedPerformance?.vendor_name}`
  }

  // Calculate average scores for vendors
  const getVendorStats = () => {
    const stats = {}
    performances.forEach((p) => {
      if (!stats[p.vendor]) {
        stats[p.vendor] = { total: 0, count: 0 }
      }
      stats[p.vendor].total += p.rating
      stats[p.vendor].count += 1
    })
    return stats
  }

  return (
    <Box>
      <PageHeader
        title="Vendors"
        subtitle="Manage service providers, suppliers, and performance reviews"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Vendors' },
        ]}
        actions={
          <>
            <ExportButtons
              data={activeTab === 0 ? vendors : performances}
              columns={activeTab === 0 ? vendorExportColumns : performanceExportColumns}
              title={activeTab === 0 ? 'Vendors List' : 'Performance Reviews'}
              filename={activeTab === 0 ? 'vendors' : 'vendor_performance'}
              filters={activeTab === 0 ? getActiveFilterLabels() : {}}
            />
            {activeTab === 0 && (
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                color={hasActiveFilters ? 'primary' : 'inherit'}
              >
                Filters {hasActiveFilters && `(${Object.values(filters).filter(v => v !== '').length})`}
              </Button>
            )}
            <Button startIcon={<Add />} onClick={handleAddClick}>
              {getAddButtonText()}
            </Button>
          </>
        }
      />

      {/* Filters Section - Only for Vendors tab */}
      {activeTab === 0 && showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Vendor Type</InputLabel>
              <Select
                value={filters.vendor_type}
                onChange={handleFilterChange('vendor_type')}
                label="Vendor Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="supplier">Supplier</MenuItem>
                <MenuItem value="service_provider">Service Provider</MenuItem>
                <MenuItem value="contractor">Contractor</MenuItem>
                <MenuItem value="oem">OEM</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.is_active}
                onChange={handleFilterChange('is_active')}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              {Object.entries(getActiveFilterLabels()).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="small"
                  onDelete={() => {
                    const fieldMap = { 'Type': 'vendor_type', 'Status': 'is_active' }
                    setFilters(prev => ({ ...prev, [fieldMap[key]]: '' }))
                  }}
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="Vendors" icon={<Star />} iconPosition="start" />
          <Tab label="Performance Reviews" icon={<Assessment />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <DataTable
            columns={[...vendorColumns, vendorActionsColumn]}
            data={vendors}
            loading={loading}
            searchPlaceholder="Search vendors..."
            emptyMessage="No vendors found"
            serverSide
            totalCount={totalCount}
            page={page}
            rowsPerPage={pageSize}
            onPaginationChange={handlePaginationChange}
            onSearch={handleSearch}
          />
        )}

        {activeTab === 1 && (
          <DataTable
            columns={[...performanceColumns, performanceActionsColumn]}
            data={performances}
            loading={loading}
            searchPlaceholder="Search performance reviews..."
            emptyMessage="No performance reviews found"
          />
        )}
      </Card>

      <VendorForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setSelectedVendor(null)
        }}
        vendor={selectedVendor}
        onSuccess={handleFormSuccess}
      />

      {/* Performance Review Form Modal */}
      <Modal
        open={performanceFormOpen}
        onClose={() => setPerformanceFormOpen(false)}
        title={selectedPerformance ? 'Edit Performance Review' : 'Add Performance Review'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setPerformanceFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePerformance} loading={saving}>
              {selectedPerformance ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={performanceFormData.vendor}
                onChange={(e) => setPerformanceFormData({ ...performanceFormData, vendor: e.target.value })}
                label="Vendor"
              >
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Related Work Order (Optional)</InputLabel>
              <Select
                value={performanceFormData.work_order}
                onChange={(e) => setPerformanceFormData({ ...performanceFormData, work_order: e.target.value })}
                label="Related Work Order (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {workOrders.map((wo) => (
                  <MenuItem key={wo.id} value={wo.id}>{wo.work_order_number} - {wo.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography component="legend">Overall Rating *</Typography>
            <Rating
              value={performanceFormData.rating}
              onChange={(_, newValue) => setPerformanceFormData({ ...performanceFormData, rating: newValue })}
              size="large"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography component="legend">Quality Score</Typography>
            <Rating
              value={performanceFormData.quality_score}
              onChange={(_, newValue) => setPerformanceFormData({ ...performanceFormData, quality_score: newValue })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography component="legend">Cost Accuracy</Typography>
            <Rating
              value={performanceFormData.cost_accuracy}
              onChange={(_, newValue) => setPerformanceFormData({ ...performanceFormData, cost_accuracy: newValue })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Response Time (hours)"
              type="number"
              inputProps={{ step: '0.5', min: 0 }}
              value={performanceFormData.response_time_hours}
              onChange={(e) => setPerformanceFormData({ ...performanceFormData, response_time_hours: e.target.value })}
              helperText="How quickly did the vendor respond?"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Comments"
              value={performanceFormData.comments}
              onChange={(e) => setPerformanceFormData({ ...performanceFormData, comments: e.target.value })}
              multiline
              rows={3}
              placeholder="Describe the vendor's performance..."
            />
          </Grid>
        </Grid>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={`Delete ${deleteType === 'vendor' ? 'Vendor' : 'Performance Review'}`}
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete "{getDeleteItemName()}"?
      </Modal>
    </Box>
  )
}

export default VendorsList
